import { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker, type BlockerFunction, type Location } from "react-router";

export interface GuardiaDeCambios {
  /** Hay una salida esperando confirmación: es lo que abre el aviso. */
  bloqueado: boolean;
  /**
   * Qué se detuvo, para que el aviso pueda decirlo con precisión: irse de la
   * pantalla no es lo mismo que cambiar de pestaña dentro de ella.
   */
  motivo: "navegacion" | "accion" | null;
  /** Deja pasar la salida pendiente (descartando, o después de guardar). */
  continuar: () => void;
  /** Cancela la salida y se queda donde estaba. */
  cancelar: () => void;
  /**
   * Apaga la guardia para lo que resta de vida de la pantalla, de inmediato y
   * sin esperar un re-render.
   *
   * Es para la navegación que sigue a un guardado exitoso: ahí ya no hay nada
   * que perder, pero `hayCambios` viene de estado y todavía no se actualizó
   * cuando se llama a `navigate`, así que la guardia frenaría su propio éxito.
   * No se vuelve a armar: la pantalla que la apaga es porque se está yendo.
   */
  desactivar: () => void;
}

/**
 * Guardia de salida para una pantalla con cambios sin guardar
 * (regla transversal de UI, `prototipado/fase1-spec.md` §13).
 *
 * Cubre las DOS formas de abandonar el trabajo:
 *
 * 1. **Navegar dentro de la app** — menú lateral, migas, "volver", `navigate()`
 *    y el botón "atrás" del navegador. Lo hace `useBlocker`, que intercepta en
 *    el ROUTER y no en cada enlace: por eso alcanza con montar el hook una vez
 *    en la pantalla en vez de envolver a mano cada salida (y por eso ninguna
 *    salida nueva se cuela sin aviso). Requiere el data router de `main.tsx`.
 * 2. **Cerrar o recargar la pestaña del navegador** — `beforeunload`. El
 *    navegador muestra SU propio cuadro, no el nuestro: el texto no se puede
 *    personalizar desde hace años. Se registra solo cuando hay cambios porque
 *    un listener permanente desactiva el bfcache de la pestaña.
 *
 * YA NO HAY UNA TERCERA FORMA. La había: `interceptar`, para lo que el router
 * no podía ver —cambiar de pestaña del editor de informes desmontaba el
 * formulario y se llevaba lo escrito, aunque la URL no se moviera—. Desde que
 * esa pestaña viaja en la URL
 * (`odd/tasks/pestanas-del-informe-en-la-url.md`), cambiarla ES una
 * navegación: el router la ve como cualquier otra y `useBlocker` alcanza
 * solo. `interceptar` se quedó sin un solo llamador y se retiró con su estado
 * `pendiente`.
 *
 * Y retirarlo no fue solo prolijidad: `pendiente.ejecutar()` hacía un
 * `navigate()` NUEVO al confirmar, y ese `navigate` volvía a pasar por
 * `debeBloquear` —con `hayCambios` todavía en `true`, porque el `setState`
 * que lo apaga es asíncrono—, así que esa navegación se bloqueaba A SÍ MISMA
 * y la pantalla quedaba sin poder cambiar de pestaña tras guardar. La salida
 * por el router (`blocker.proceed()`) no tiene ese problema: continúa la
 * navegación YA bloqueada sin volver a evaluar la condición.
 *
 * `clasificar` reemplaza lo que antes decidía `interceptar` a mano: recibe el
 * DESTINO bloqueado y devuelve el motivo. Sin él, toda salida bloqueada es
 * `"navegacion"` —es el caso de `CrearInforme`, que no tiene pestañas que
 * distinguir—. Quien sabe que un destino es "otra pestaña del mismo informe"
 * puede afinarlo para que el aviso diga con precisión qué se está por perder,
 * y eso alcanza también al botón "atrás" del navegador entre dos pestañas,
 * que antes no podía decir nada.
 *
 * El hook NO decide qué hacer con los cambios: solo detiene la salida y expone
 * `continuar`/`cancelar`. Guardar es de quien conoce el formulario.
 */
export function useGuardiaDeCambios(
  hayCambios: boolean,
  clasificar?: (destino: Location) => "navegacion" | "accion",
): GuardiaDeCambios {
  /* Referencia y estado en paralelo, y no una sola cosa: el blocker consulta la
     referencia en el mismo tick en que se pide la navegación, mientras que el
     efecto de `beforeunload` necesita el estado para volver a correr. */
  const apagada = useRef(false);
  const [desactivada, setDesactivada] = useState(false);

  const vigilando = hayCambios && !desactivada;

  const desactivar = useCallback(() => {
    apagada.current = true;
    setDesactivada(true);
  }, []);

  const debeBloquear = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      !apagada.current && hayCambios && currentLocation.pathname !== nextLocation.pathname,
    [hayCambios],
  );

  const blocker = useBlocker(debeBloquear);

  useEffect(() => {
    if (!vigilando) return;

    const avisar = (evento: BeforeUnloadEvent) => evento.preventDefault();

    window.addEventListener("beforeunload", avisar);
    return () => window.removeEventListener("beforeunload", avisar);
  }, [vigilando]);

  const continuar = useCallback(() => {
    /* `proceed` deja pasar ESA navegación aunque `hayCambios` siga en true: es
       lo que permite salir sin guardar sin tener que vaciar el formulario. */
    blocker.proceed?.();
  }, [blocker]);

  const cancelar = useCallback(() => {
    blocker.reset?.();
  }, [blocker]);

  const motivo =
    blocker.state === "blocked"
      ? clasificar
        ? clasificar(blocker.location)
        : "navegacion"
      : null;

  return {
    bloqueado: motivo !== null,
    motivo,
    continuar,
    cancelar,
    desactivar,
  };
}
